import { useState } from "react";
import { addSite } from "../services/api";

export default function MonitorForm({ onSiteAdded }) {
  const [url, setUrl] = useState("");
  const [alertEmails, setAlertEmails] = useState("");
  const [sslEnabled, setSslEnabled] = useState(true);
  const [sslWarningDays, setSslWarningDays] = useState(30);
  const [redirectEnabled, setRedirectEnabled] = useState(true);
  const [expectedDestination, setExpectedDestination] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    
    try {
      // Parse alert emails from comma-separated string
      const emails = alertEmails
        .split(',')
        .map(email => email.trim())
        .filter(email => email && email.includes('@'));

      const { data } = await addSite(url, emails, {
        sslEnabled,
        sslWarningDays,
        redirectEnabled,
        expectedDestination: expectedDestination.trim() || null
      });
      onSiteAdded(data);
      setUrl("");
      setAlertEmails("");
      setSslEnabled(true);
      setSslWarningDays(30);
      setRedirectEnabled(true);
      setExpectedDestination("");
      setShowAdvanced(false);
    } catch (error) {
      setErr(error.message || "Add failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-10 w-full">
      {/* Outer glow wrapper */}
      <div className="relative group">
        {/* Animated glow border */}
        <div className="absolute -inset-[1px] bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500 rounded-2xl opacity-60 group-hover:opacity-100 blur-sm transition-opacity duration-500" />

        <div className="relative bg-[#0b1120] rounded-2xl p-6 md:p-8 overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
          
          {/* Gradient orb decorations */}
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-cyan-500/8 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-500/30 rounded-xl blur-md" />
                <div className="relative bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-xl p-2.5">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">Add a Website to Monitor</h2>
                <p className="text-gray-400 text-sm mt-0.5">Ping every 2 min &middot; Uptime, SSL & redirect alerts</p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Main row */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
                {/* URL */}
                <div className="lg:col-span-5">
                  <label htmlFor="url" className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                    Website URL
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-emerald-500/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                    </div>
                    <input 
                      id="url"
                      type="url"
                      className="w-full pl-10 pr-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 hover:border-white/20 transition-all" 
                      value={url} 
                      onChange={e => setUrl(e.target.value)} 
                      placeholder="https://yourwebsite.com"
                      required
                    />
                  </div>
                </div>

                {/* SSL Warning Days */}
                <div className="lg:col-span-2">
                  <label htmlFor="sslWarningDays" className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                    SSL Alert
                  </label>
                  <select
                    id="sslWarningDays"
                    value={sslWarningDays}
                    onChange={e => setSslWarningDays(parseInt(e.target.value))}
                    disabled={!sslEnabled}
                    className="w-full px-3 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 hover:border-white/20 transition-all disabled:opacity-40 appearance-none cursor-pointer"
                  >
                    <option value={7}>7 days before</option>
                    <option value={14}>14 days before</option>
                    <option value={30}>30 days before</option>
                    <option value={60}>60 days before</option>
                    <option value={90}>90 days before</option>
                  </select>
                </div>

                {/* SSL Toggle */}
                <div className="lg:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                    SSL Check
                  </label>
                  <button 
                    type="button"
                    role="switch"
                    aria-checked={sslEnabled}
                    className="flex w-full items-center justify-between px-3 py-3 bg-white/[0.04] border border-white/10 rounded-xl cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50 hover:border-white/20 transition-all"
                    onClick={() => setSslEnabled(!sslEnabled)}
                  >
                    <span className={`text-sm ${sslEnabled ? 'text-emerald-400' : 'text-gray-500'} transition-colors`}>
                      {sslEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                    <div className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${sslEnabled ? 'bg-emerald-500' : 'bg-gray-600'}`}>
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${sslEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
                    </div>
                  </button>
                </div>

                {/* Submit */}
                <div className="lg:col-span-3">
                  <label className="block text-[11px] font-semibold text-transparent uppercase mb-1.5 select-none">&nbsp;</label>
                  <button 
                    type="submit"
                    disabled={busy || !url}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold py-3 px-5 rounded-xl transition-all transform hover:scale-[1.02] active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
                  >
                    {busy ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        Start Monitoring
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Advanced toggle */}
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-xs text-gray-500 hover:text-emerald-400 flex items-center gap-1.5 transition-colors group"
                >
                  <span className="w-4 h-4 rounded-md border border-gray-700 group-hover:border-emerald-500/50 flex items-center justify-center transition-colors">
                    <svg className={`w-2.5 h-2.5 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                  {showAdvanced ? 'Hide advanced options' : 'Advanced options (redirects, emails)'}
                </button>
              </div>

              {/* Advanced Section */}
              {showAdvanced && (
                <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/[0.02] p-4 rounded-xl border border-white/5">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        id="redirectEnabled"
                        type="checkbox"
                        checked={redirectEnabled}
                        onChange={e => setRedirectEnabled(e.target.checked)}
                        className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-gray-600 bg-gray-700 rounded cursor-pointer"
                      />
                      <label htmlFor="redirectEnabled" className="text-sm font-medium text-gray-200 cursor-pointer">
                        Monitor Redirects & Loops
                      </label>
                    </div>
                    {redirectEnabled && (
                      <input
                        type="url"
                        value={expectedDestination}
                        onChange={e => setExpectedDestination(e.target.value)}
                        placeholder="Expected destination URL (optional)"
                        className="w-full mt-1 px-3 py-2 bg-white/[0.04] border border-white/10 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all"
                      />
                    )}
                  </div>

                  <div className="bg-white/[0.02] p-4 rounded-xl border border-white/5">
                    <label className="block text-xs font-medium text-gray-400 mb-2">Additional Alert Emails</label>
                    <input
                      type="text"
                      value={alertEmails}
                      onChange={e => setAlertEmails(e.target.value)}
                      placeholder="colleague@example.com, team@company.com"
                      className="w-full px-3 py-2 bg-white/[0.04] border border-white/10 rounded-lg text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all"
                    />
                  </div>
                </div>
              )}

              {/* Info footer */}
              <div className="mt-5 flex items-center gap-2 text-[13px] text-gray-500">
                <svg className="w-3.5 h-3.5 text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Alerts go to your account email + any additional emails.{" "}
                <a href="/profile" className="text-emerald-500/80 hover:text-emerald-400 transition-colors">Manage in Profile →</a>
              </div>

              {err && (
                <div className="mt-4 rounded-xl bg-red-500/10 border border-red-500/20 p-4">
                  <div className="text-sm text-red-400 flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {err}
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
